import { memo, useMemo } from "react";
import { connect } from "react-redux";

import { DesignToken } from "@livechat/design-system-react-components";

import { IDateRangeWithCompareFilter } from "constants/filters";
import { ReportDistribution } from "constants/reports/distribution";
import { ReportType } from "constants/reports/report-type";
import { useLastVisitedReport } from "hooks/reports/use-last-visited-report";
import { IStoreState } from "interfaces/storeState";
import { ReportLocalTimeLabel } from "routes/reports/components/ReportLocaltimeLabel";
import {
  IHeatmapData,
  INewTicketsData,
  getDistribution,
  getFilterDateRange,
  getIsReportFetching,
  getReportData,
  selectIsHeatmapEnabled,
} from "store/views/reports";
import { withVar } from "themes/helpers";

import { ChartPanel, Filters } from "../components";
import { Heatmap } from "../components/HeatmapChart";
import { getChartOptions, getExportFilename } from "../helpers";
import { containerFluid } from "../styled/layout";

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type, @typescript-eslint/no-explicit-any
function getNewTicketsHeatmapOptions(data: any) {
  const { chartSeries, chartLabels } = data;
  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const sumAll = chartSeries.reduce((acc: any, curr: any) => acc + curr.value, 0);

  return {
    color: {
      minColor: withVar(sumAll ? DesignToken.DecorGreen100 : DesignToken.SurfaceSecondaryDefault),
      maxColor: withVar(sumAll ? DesignToken.DecorGreen100 : DesignToken.SurfaceSecondaryDefault),
    },
    legend: {
      title: "Number of tickets",
    },
    series: {
      name: "Tickets",
      data: chartSeries,
    },
    categories: chartLabels,
  };
}

interface IStateProps {
  isFetching: boolean;
  isFetchingHeatmap: boolean;
  isHeatmapEnabled: boolean;
  data: INewTicketsData;
  heatmapData: IHeatmapData;
  distribution: ReportDistribution;
  dateRange: IDateRangeWithCompareFilter;
}

type IProps = IStateProps;
const sourcesChartOptions = {
  forceOptions: {
    fixedSeriesHeader: "Source",
  },
};

const NewTickets = memo(function NewTickets({
  isFetching,
  isFetchingHeatmap,
  isHeatmapEnabled,
  data,
  heatmapData,
  distribution,
  dateRange,
}: IProps) {
  useLastVisitedReport(ReportType.NewTickets);
  const newTicketsChartLegend = useMemo(() => data && data.newTickets.summary, [data]);
  const newTicketsChartOptions = useMemo(
    () => data && getChartOptions(data.newTickets.series, data.newTickets.labels, distribution, null),
    [data, distribution]
  );

  const ticketSourcesChartLegend = useMemo(() => data && data.ticketSources.summary, [data]);
  const ticketSourcesChartOptions = useMemo(
    () =>
      data &&
      getChartOptions(data.ticketSources.series, data.ticketSources.labels, distribution, null, sourcesChartOptions),
    [data, distribution]
  );

  const newTicketsHeatmapOptions = useMemo(
    () => heatmapData && getNewTicketsHeatmapOptions(heatmapData),
    [heatmapData]
  );

  return (
    <div>
      <Filters />
      <div css={containerFluid}>
        <ChartPanel
          title="New tickets"
          tooltip="The number of new tickets created in a given time, excluding tickets in the Spam and Trash folders."
          loading={isFetching}
          legend={newTicketsChartLegend}
          options={newTicketsChartOptions}
          exportFileName={getExportFilename("new_tickets", dateRange, distribution)}
        />
        <ChartPanel
          title="Ticket sources"
          loading={isFetching}
          legend={ticketSourcesChartLegend}
          options={ticketSourcesChartOptions}
          exportFileName={getExportFilename("ticket_sources", dateRange, distribution)}
        />
        {isHeatmapEnabled && (
          <Heatmap
            title="New tickets heatmap in last 7 days"
            heatmapTooltipName="new tickets"
            loading={isFetchingHeatmap}
            options={newTicketsHeatmapOptions}
            exportFileName={getExportFilename("heatmap_new_tickets", dateRange, distribution)}
          />
        )}
        <ReportLocalTimeLabel />
      </div>
    </div>
  );
});

const mapStateToProps = (state: IStoreState): IStateProps => ({
  isFetching: getIsReportFetching(state, ReportType.NewTickets),
  isFetchingHeatmap: getIsReportFetching(state, ReportType.NewTicketsHeatmap),
  isHeatmapEnabled: selectIsHeatmapEnabled(state),
  // @ts-expect-error TS(2322)
  data: getReportData(state, ReportType.NewTickets),
  // @ts-expect-error TS(2322)
  heatmapData: getReportData(state, ReportType.NewTicketsHeatmap),
  distribution: getDistribution(state),
  dateRange: getFilterDateRange(state),
});

export default connect(mapStateToProps, null)(NewTickets);
